import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import AppModal from '../../common/Modal';
import FormField from '../../common/FormField';
import { RATING_MAX, RATING_MIN } from '../../common-questions/TRating';
import { useLanguage } from '../../../assets/js/LanguageContext';
import '../../../assets/styles/TemplateBuilder.css';

const createQuestionState = (source = {}) => {
  const tipo = source.tipo || 'open';
  const state = {
    ...source,
    tipo,
    pregunta: source.pregunta || '',
    minValue: source.minValue ?? 0,
    maxValue: source.maxValue ?? 10,
  };

  if (tipo === 'closed') {
    state.opciones = Array.isArray(source.opciones) && source.opciones.length ? source.opciones : ['', ''];
  }

  return state;
};

const QuestionEditorModal = ({ question, onClose, onSave }) => {
  const { t } = useLanguage();
  const [edited, setEdited] = useState(createQuestionState(question || {}));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (question) {
      setEdited(createQuestionState(question));
      setError(null);
    }
  }, [question]);

  const typeLabel = {
    open: t('question_type_open_full'),
    closed: t('question_type_closed_full'),
    rating: t('question_type_rating_full'),
  }[edited.tipo] || t('common_question');

  const setOption = (index, value) => {
    const options = [...(edited.opciones || [])];
    options[index] = value;
    setEdited({ ...edited, opciones: options });
  };

  const addOption = () => {
    setEdited({ ...edited, opciones: [...(edited.opciones || []), ''] });
  };

  const removeOption = (index) => {
    const options = edited.opciones || [];
    if (options.length <= 2) return;
    setEdited({ ...edited, opciones: options.filter((_, i) => i !== index) });
  };

  const handleSave = () => {
    const statement = (edited.pregunta || '').trim();
    if (!statement) {
      setError(t('question_statement_required'));
      return false;
    }

    const min = parseInt(edited.minValue, 10);
    const max = parseInt(edited.maxValue, 10);

    if (edited.tipo === 'rating') {
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
        setError(t('question_range_invalid'));
        return false;
      }
      if (min < RATING_MIN || max > RATING_MAX) {
        setError(t('question_range_limit').replace('{min}', RATING_MIN).replace('{max}', RATING_MAX));
        return false;
      }
    }

    const options = (edited.opciones || []).map((option) => option.trim()).filter(Boolean);
    if (edited.tipo === 'closed' && options.length < 2) {
      setError(t('question_options_required'));
      return false;
    }

    onSave({
      ...edited,
      pregunta: statement,
      minValue: edited.tipo === 'rating' ? min : undefined,
      maxValue: edited.tipo === 'rating' ? max : undefined,
      opciones: edited.tipo === 'closed' ? options : undefined,
    });
    return true;
  };

  return (
    <AppModal
      open={Boolean(question)}
      onOpenChange={(open) => { if (!open) onClose(); }}
      variant="edit"
      title={question && question.configurada ? t('question_edit_title') : t('question_new')}
      description={typeLabel}
      cancelText={t('common_cancel')}
      confirmText={t('dash_save')}
      onCancel={onClose}
      onConfirm={handleSave}
      size="md"
      className="tp-qedit-modal"
    >
      <div className="tp-form tp-form--bare tp-qedit">
        <FormField
          id="question-statement"
          label={t('question_statement')}
          as="textarea"
          value={edited.pregunta || ''}
          onChange={(event) => { setEdited({ ...edited, pregunta: event.target.value }); setError(null); }}
          placeholder={t('question_participant_placeholder')}
          rows={3}
        />

        {edited.tipo === 'closed' && (
          <div className="tp-qedit__block">
            <p className="tp-qedit__label">{t('question_answer_options')}</p>
            <ul className="tp-qedit__options">
              {(edited.opciones || []).map((option, index) => (
                <li key={index} className="tp-qedit__option">
                  <FormField
                    id={`question-option-${index}`}
                    label={`${t('common_option')} ${index + 1}`}
                    hideLabel
                    type="text"
                    value={option}
                    placeholder={`${t('common_option')} ${index + 1}`}
                    onChange={(event) => { setOption(index, event.target.value); setError(null); }}
                  />
                  <button
                    type="button"
                    className="tp-qedit__remove"
                    onClick={() => removeOption(index)}
                    disabled={(edited.opciones || []).length <= 2}
                    aria-label={t('question_delete_option')}
                  >
                    <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="btn btn--secondary btn--sm" onClick={addOption}>
              <Plus size={14} strokeWidth={2.25} aria-hidden="true" />
              {t('question_add_option')}
            </button>
          </div>
        )}

        {edited.tipo === 'rating' && (
          <div className="tp-qedit__block">
            <p className="tp-qedit__label">{t('question_rating_range')}</p>
            <div className="tp-qedit__range">
              <FormField
                id="question-min"
                label={t('question_minimum')}
                type="number"
                min={RATING_MIN}
                max={RATING_MAX - 1}
                value={edited.minValue}
                onChange={(event) => { setEdited({ ...edited, minValue: event.target.value }); setError(null); }}
              />
              <FormField
                id="question-max"
                label={t('question_maximum')}
                type="number"
                min={RATING_MIN + 1}
                max={RATING_MAX}
                value={edited.maxValue}
                onChange={(event) => { setEdited({ ...edited, maxValue: event.target.value }); setError(null); }}
              />
            </div>
            <p className="tp-qedit__hint">
              {t('question_rating_hint').replace('{min}', edited.minValue).replace('{max}', edited.maxValue)}
              {' '}
              {t('question_rating_cap').replace('{max}', RATING_MAX)}
            </p>
          </div>
        )}

        {error && <p className="tp-form__alert" role="alert">{error}</p>}
      </div>
    </AppModal>
  );
};

export default QuestionEditorModal;
